import { normalizeItemName } from "./normalizeItemName";

const DATE_REGEX = /\bdate\s*[:\uFF1A]\s*(\d{4}-\d{2}-\d{2})\b/i;
const SENT_REGEX = /\b(items?\s+sent|sent)\s*[:\uFF1A]\s*(.+)$/i;
const RECEIVED_REGEX = /\b(items?\s+received|received)\s*[:\uFF1A]\s*(.+)$/i;
const NOTES_REGEX = /\bnotes\s*[:\uFF1A]\s*(.+)$/i;

const cleanLine = (line) =>
  line
    .replace(/\uFEFF/g, "")
    .replace(/\u200B/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();

const getCaliforniaToday = () => {
  const now = new Date();
  const pst = new Date(
    now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
  );
  pst.setHours(0, 0, 0, 0);
  return pst;
};

const isValidTradeDate = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);

  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d &&
    date <= getCaliforniaToday()
  );
};

export const normalizeTradeItems = (itemString) => {
  if (!itemString) return itemString;

  return itemString
    .split("+")
    .map((entry) => {
      const trimmed = entry.trim();
      const match = trimmed.match(/^(.*?)(\s*\([^)]+\))$/);

      if (!match) return normalizeItemName(trimmed);

      const [, rawName, value] = match;
      const normalized = normalizeItemName(rawName.trim());
      return `${normalized}${value}`;
    })
    .join(" + ");
};

const blockTags = new Set([
  "DIV",
  "P",
  "LI",
  "UL",
  "OL",
  "TABLE",
  "TR",
  "TD",
  "SECTION",
  "ARTICLE",
  "HEADER",
  "FOOTER",
  "CENTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);

const nodeToText = (node, parts) => {
  if (!node) return;

  if (node.nodeType === Node.TEXT_NODE) {
    parts.push(node.nodeValue || "");
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tagName = node.tagName?.toUpperCase();

  if (tagName === "BR") {
    parts.push("\n");
    return;
  }

  if (tagName === "IMG") {
    const alt = cleanLine(node.getAttribute("alt") || "");
    if (alt) parts.push(` ${alt} `);
    return;
  }

  if (blockTags.has(tagName)) parts.push("\n");
  node.childNodes.forEach((child) => nodeToText(child, parts));
  if (blockTags.has(tagName)) parts.push("\n");
};

const extractMessageLines = (messageNode) => {
  const parts = [];
  nodeToText(messageNode, parts);

  return parts
    .join("")
    .replace(/\r/g, "")
    .split("\n")
    .map(cleanLine)
    .filter(Boolean);
};

const makeTradeDraft = (tradeDate, dateLine) => ({
  tradeDate,
  itemsSent: "",
  itemsReceived: "",
  notes: "",
  rawLines: [dateLine],
  extraLines: [],
  trailingLines: [],
});

const finalizeTrade = (draft, baseMeta, validateItems) => {
  const reasons = [];

  if (!draft.itemsSent) reasons.push("Missing sent line");
  if (!draft.itemsReceived) reasons.push("Missing received line");
  if (!draft.notes) reasons.push("Missing notes line");
  if (!isValidTradeDate(draft.tradeDate)) reasons.push("Invalid or future trade date");
  if (draft.extraLines.length) {
    reasons.push(`Unexpected text inside trade block: ${draft.extraLines.join(" | ")}`);
  }

  const normalizedSent = normalizeTradeItems(draft.itemsSent);
  const normalizedReceived = normalizeTradeItems(draft.itemsReceived);

  if (normalizedSent) {
    const sentValidation = validateItems(normalizedSent);
    if (sentValidation.errors.length) {
      reasons.push(`Items sent: ${sentValidation.errors.join("; ")}`);
    }
  }

  if (normalizedReceived) {
    const receivedValidation = validateItems(normalizedReceived);
    if (receivedValidation.errors.length) {
      reasons.push(`Items received: ${receivedValidation.errors.join("; ")}`);
    }
  }

  return {
    ...baseMeta,
    trade_date: draft.tradeDate,
    items_sent: normalizedSent,
    items_received: normalizedReceived,
    notes: draft.notes,
    raw_text: draft.rawLines.join("\n"),
    reasons,
  };
};

const parseTradesFromLines = (lines, baseMeta, validateItems) => {
  const trades = [];
  let currentTrade = null;

  for (const line of lines) {
    const dateMatch = line.match(DATE_REGEX);

    if (dateMatch) {
      if (currentTrade) {
        trades.push(finalizeTrade(currentTrade, baseMeta, validateItems));
      }
      currentTrade = makeTradeDraft(dateMatch[1], line);
      continue;
    }

    if (!currentTrade) continue;

    currentTrade.rawLines.push(line);

    const sentMatch = line.match(SENT_REGEX);
    if (sentMatch && !currentTrade.itemsSent) {
      currentTrade.itemsSent = sentMatch[2].trim();
      continue;
    }

    const receivedMatch = line.match(RECEIVED_REGEX);
    if (receivedMatch && !currentTrade.itemsReceived) {
      currentTrade.itemsReceived = receivedMatch[2].trim();
      continue;
    }

    const notesMatch = line.match(NOTES_REGEX);
    if (notesMatch && !currentTrade.notes) {
      currentTrade.notes = notesMatch[1].trim();
      continue;
    }

    if (currentTrade.notes) {
      currentTrade.trailingLines.push(line);
    } else {
      currentTrade.extraLines.push(line);
    }
  }

  if (currentTrade) {
    trades.push(finalizeTrade(currentTrade, baseMeta, validateItems));
  }

  return trades;
};

const collectPostNodes = (doc) => {
  const listItems = Array.from(doc.querySelectorAll("li")).filter(
    (node) => node.querySelector(".postAuthorName") && node.querySelector(".boardPostMessage")
  );

  if (listItems.length) return listItems;

  return Array.from(doc.querySelectorAll(".boardPostMessage"))
    .map((messageNode) => messageNode.closest("li") || messageNode.parentElement)
    .filter(Boolean);
};

const extractPostMeta = (postNode) => {
  const username = cleanLine(
    postNode.querySelector(".postAuthorName")?.textContent || "unknown"
  );
  const postId = cleanLine(
    postNode.querySelector(".postAuthorInfo a[name]")?.getAttribute("name") || ""
  );
  const postedAt = cleanLine(
    postNode.querySelector(".boardPostDate")?.textContent || ""
  );
  const messageNode = postNode.querySelector(".boardPostMessage");
  const lines = extractMessageLines(messageNode);

  return {
    username,
    source_post_id: postId,
    source_posted_at: postedAt,
    message_lines: lines,
  };
};

const splitIntoHtmlDocuments = (rawHtml) => {
  const normalized = rawHtml.replace(/\r/g, "");
  const doctypeMatches = [...normalized.matchAll(/<!doctype html>/gi)].map(
    (match) => match.index
  );

  if (doctypeMatches.length <= 1) {
    return [normalized];
  }

  return doctypeMatches.map((start, index) => {
    const end = doctypeMatches[index + 1] ?? normalized.length;
    return normalized.slice(start, end).trim();
  });
};

export const parseNeoboardsSource = (rawHtml, validateItems) => {
  if (!rawHtml || !rawHtml.trim()) {
    return {
      validTrades: [],
      reviewTrades: [],
      skippedPosts: [],
      summary: {
        totalPosts: 0,
        postsWithTrades: 0,
        skippedPosts: 0,
        totalTrades: 0,
        validTrades: 0,
        reviewTrades: 0,
      },
    };
  }

  const parser = new DOMParser();
  const documents = splitIntoHtmlDocuments(rawHtml);
  const seenPostIds = new Set();
  const postNodes = [];

  documents.forEach((htmlChunk) => {
    const doc = parser.parseFromString(htmlChunk, "text/html");
    collectPostNodes(doc).forEach((postNode) => {
      const postId =
        cleanLine(
          postNode.querySelector(".postAuthorInfo a[name]")?.getAttribute("name") || ""
        ) || `no-post-id-${postNodes.length}`;

      if (seenPostIds.has(postId)) {
        return;
      }

      seenPostIds.add(postId);
      postNodes.push(postNode);
    });
  });

  const allTrades = [];
  const skippedPosts = [];
  let postsWithTrades = 0;

  postNodes.forEach((postNode) => {
    const meta = extractPostMeta(postNode);

    if (!meta.message_lines.length) {
      return;
    }

    const trades = parseTradesFromLines(
      meta.message_lines,
      {
        username: meta.username,
        source_post_id: meta.source_post_id,
        source_posted_at: meta.source_posted_at,
      },
      validateItems
    );

    if (!trades.length) {
      skippedPosts.push({
        username: meta.username,
        source_post_id: meta.source_post_id,
        source_posted_at: meta.source_posted_at,
        excerpt: meta.message_lines.slice(0, 4).join(" ").slice(0, 240),
      });
      return;
    }

    postsWithTrades += 1;
    allTrades.push(...trades);
  });

  const validTrades = allTrades.filter((trade) => trade.reasons.length === 0);
  const reviewTrades = allTrades.filter((trade) => trade.reasons.length > 0);

  return {
    validTrades,
    reviewTrades,
    skippedPosts,
    summary: {
      totalPosts: postNodes.length,
      postsWithTrades,
      skippedPosts: skippedPosts.length,
      totalTrades: allTrades.length,
      validTrades: validTrades.length,
      reviewTrades: reviewTrades.length,
    },
  };
};
